import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import ko from '../language/Ko-kr.json';
import en from '../language/En-eng.json';
import { ResponsiveShell } from '../components/ResponsiveShell';

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0.5; }
  to { transform: translateY(0); opacity: 1; }
`;

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
  z-index: 10;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
  color: #5C5040;
  &:hover { background: #E0D8C0; transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
  text-align: center;
  margin-right: 36px; /* offset for back button */
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 32px 24px;
  gap: 24px;
`;

/* ─── Profile Area ───────────────────────────────────────────────────────── */
const ProfileHeaderCard = styled.div`
  background: #FFF;
  border: 1.5px solid #D8D0B8;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 12px rgba(90, 60, 30, 0.05);
`;

const ProfileImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #E8E0C8;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #D8D0B8;
  color: #B0A080;
  
  svg {
    width: 40px;
    height: 40px;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const UserName = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #3D3020;
`;

const UserDetail = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 13px;
  color: #8C8070;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* ─── Actions Area ───────────────────────────────────────────────────────── */
const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionBtn = styled.button`
  background: #FFF;
  border: 1.5px solid #D8D0B8;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #F9F6E8;
    border-color: #C8C0A8;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ActionText = styled.span`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #5C5040;
`;

const ChevronIcon = styled.div`
  color: #C8C0A8;
  display: flex;
  align-items: center;
`;

/* ─── Modal ──────────────────────────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ModalContent = styled.div`
  background: #F9F6E8;
  padding: 24px;
  border-radius: 20px;
  width: 80%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.2);
`;

const ModalTitle = styled.h3`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  text-align: center;
`;

const ModalInput = styled.input`
  width: 100%;
  border: 1.5px solid #D8D0B8;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  outline: none;
  
  &:focus {
    border-color: #E07040;
  }
`;

const ModalBtnGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: none;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${p => p.$primary ? '#E07040' : '#EDE9D5'};
  color: ${p => p.$primary ? '#FFF' : '#5C5040'};
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
const Profile: React.FC = () => {
  const [userInfo, setUserInfo] = useState<{ name: string; email: string; alias: string } | null>(null);
  const [editMode, setEditMode] = useState<'none' | 'name' | 'password'>('none');
  const [editValue, setEditValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [popup, setPopup] = useState<{ title: string; msg: string; onClose?: () => void } | null>(null);
  const showPopup = (title: string, msg: string, onClose?: () => void) => setPopup({ title, msg, onClose });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        if (!token) {
          showPopup('프로필 안내', t.profile.loginRequired, () => { window.location.href = '/start'; });
          return;
        }

        // 로그인 토큰을 헤더에 담아서 유저 정보 조회
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(() => null);

        if (response && response.ok) {
          const data = await response.json();
          setUserInfo({
            name: data.user_name || '이름 없음',
            email: data.user_email || '이메일 없음',
            alias: data.user_alias || '@unknown'
          });
        } else {
          // API 에러 또는 DB 미준비 시 더미 데이터로 폴백
          console.warn("DB is not ready or API failed. Using dummy profile data.");
          setUserInfo({
            name: '테스트 유저',
            email: 'test_email@example.com',
            alias: '@test_user'
          });
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
        setUserInfo({
          name: '테스트 유저',
          email: 'test_email@example.com',
          alias: '@test_user'
        });
      }
    };

    fetchProfile();
  }, []);

  const handleUpdate = async () => {
    if (!editValue.trim()) {
      showPopup('프로필 안내', t.profile.enterValue);
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token) return;

      const payload = editMode === 'name' ? { user_name: editValue } : { password: editValue };

      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showPopup('프로필 수정', t.profile.updateSuccess, () => {
          if (editMode === 'name' && userInfo) setUserInfo({ ...userInfo, name: editValue });
          setEditMode('none');
          setEditValue('');
        });
      } else {
        const err = await response.json().catch(() => null);
        showPopup('프로필 안내', t.profile.updateFail + (err?.detail || ''));
      }
    } catch (err) {
      console.error(err);
      showPopup('프로필 안내', t.profile.updateError);
    } finally {
      setIsUpdating(false);
    }
  };

  const displayUser = userInfo || { name: t.profile.loading, email: '', alias: '' };

  return (
    <>
      <GlobalStyle />
      <ResponsiveShell>
        <PhoneNavBar>
          <BackBtn onClick={() => { window.location.href = '/'; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </BackBtn>
          <NavTitle>{t.profile.title}</NavTitle>
        </PhoneNavBar>

        <PhoneBody>
          <ProfileHeaderCard>
            <ProfileImage>
              {/* 더미 이미지 아이콘 */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </ProfileImage>
            
            <ProfileInfo>
              <UserName>{displayUser.name}</UserName>
              <UserDetail>
                <span>{displayUser.email}</span>
                <span>{displayUser.alias}</span>
              </UserDetail>
            </ProfileInfo>
          </ProfileHeaderCard>

          <ActionList>
            <ActionBtn onClick={() => { setEditMode('name'); setEditValue(''); }}>
              <ActionText>{t.profile.changeName}</ActionText>
              <ChevronIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </ChevronIcon>
            </ActionBtn>

            <ActionBtn onClick={() => { setEditMode('password'); setEditValue(''); }}>
              <ActionText>{t.profile.changePassword}</ActionText>
              <ChevronIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </ChevronIcon>
            </ActionBtn>
          </ActionList>

        </PhoneBody>

        {editMode !== 'none' && (
          <ModalOverlay>
            <ModalContent>
              <ModalTitle>
                {editMode === 'name' 
                  ? t.profile.changeName 
                  : t.profile.changePassword}
              </ModalTitle>
              <ModalInput 
                type={editMode === 'password' ? 'password' : 'text'}
                placeholder={editMode === 'name' ? t.profile.namePlaceholder : t.profile.passwordPlaceholder}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
              />
              <ModalBtnGroup>
                <ModalBtn onClick={() => { setEditMode('none'); setEditValue(''); }}>
                  {t.profile.cancel}
                </ModalBtn>
                <ModalBtn $primary onClick={handleUpdate} disabled={isUpdating}>
                  {isUpdating ? '...' : t.profile.save}
                </ModalBtn>
              </ModalBtnGroup>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* ── 인라인 알림 팝업 ── */}
        {popup && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(40,30,20,0.55)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
              width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14, color: '#E07040'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>{popup.title}</p>
              <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22, whiteSpace: 'pre-line' }}>{popup.msg}</p>
              <button
                onClick={() => { const cb = popup.onClose; setPopup(null); cb?.(); }}
                style={{
                  width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                  fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                }}
              >확인</button>
            </div>
          </div>
        )}
      </ResponsiveShell>
    </>
  );
};

export default Profile;
